import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { API_URL, apiBase } from "./api/client";
import { createApiSource } from "./data/api";
import { createApiLibrary, createMockLibrary, LibraryContext } from "./data/catalogueSource";
import { createMockSource } from "./data/mock";
import { PlanContext } from "./data/planQueries";
import { createApiPlans } from "./data/plansApi";
import { createApiStudy } from "./data/studyApi";
import { StudyContext } from "./data/studyQueries";
import { ApiError } from "./data/problem";
import { keys, SourceContext } from "./data/queries";
import { showError, ToastProvider } from "./components/Toast";
import { MotionProvider } from "./motion/MotionProvider";
import { routeTree } from "./routeTree.gen";
import "./styles/motion.css";
import "./styles/tokens.css";
import "./styles/base.css";

// VITE_API_URL set: the real API (see README, "Run it against the local API"). Unset: the prototype data.
const source = API_URL ? createApiSource({ baseUrl: apiBase() }) : createMockSource();
// Reading plans on the API too; the prototype's own otherwise (planQueries.ts falls back to it).
const plans = API_URL ? createApiPlans({ baseUrl: apiBase() }) : null;
// The Bible map from the plans read on the API; reflections are synced notes (studyApi.ts).
const study = plans ? createApiStudy(plans) : null;
// The library: the API's catalogue, entitlements and packages; or the static catalogue on the prototype.
const library = API_URL ? createApiLibrary({ baseUrl: apiBase() }) : createMockLibrary();

/** A session that ended (signed out elsewhere, expired): back to sign-in, keeping the page to return to. */
const onError = (e: unknown) => {
  if (e instanceof ApiError && e.status === 401 && !location.pathname.startsWith("/sign-in")) {
    queryClient.setQueryData(keys.session, null);
    void router.navigate({ to: "/sign-in", search: { next: location.pathname } });
    return true;
  }
  return false;
};

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: (n, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && n < 1 } },
  queryCache: new QueryCache({ onError }),
  // A refused change says why, in words (problem+json → problem.ts), unless the screen shows it beside the field.
  mutationCache: new MutationCache({
    onError: (e, _v, _c, mutation) => {
      if (!onError(e) && !mutation.meta?.inline) showError((e as Error).message);
    },
  }),
});

const router = createRouter({
  routeTree,
  context: { queryClient, source },
  defaultPreload: "intent",
  scrollRestoration: true,
  // Pages cross-fade, and a verse's quote glides from the feed into its post page (CSS view-transition-name).
  // Not on the first load (BS-WEB-BUG-20): there is no page to fade from, and while a transition
  // runs Chrome sends every press to <html>, so a tap in the first half second went nowhere.
  defaultViewTransition: { types: ({ fromLocation }) => (fromLocation ? [] : false) },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <SourceContext.Provider value={source}>
      <LibraryContext.Provider value={library}>
      <PlanContext.Provider value={plans}>
        <StudyContext.Provider value={study}>
          <QueryClientProvider client={queryClient}>
            <MotionProvider>
              <ToastProvider>
                <RouterProvider router={router} />
              </ToastProvider>
            </MotionProvider>
          </QueryClientProvider>
        </StudyContext.Provider>
      </PlanContext.Provider>
      </LibraryContext.Provider>
    </SourceContext.Provider>
  </StrictMode>,
);
